import { useState, useEffect, useCallback } from 'react';
import { useChat } from '../../context/ChatContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { FormulaTooltip, resolveMetricKey } from '../FormulaTooltip';
import { 
  AreaChart, Area, BarChart, Bar, LineChart, Line, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend, PieChart, Pie
} from 'recharts';
import { 
  Coins,
  Wallet,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Users,
  Zap,
  Clock,
  AlertTriangle,
  DollarSign,
  Activity,
  Sparkles,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Shield,
  Target,
  PiggyBank,
  Gem,
  Scale,
  Timer,
  AlertCircle,
  CheckCircle,
  ZoomIn
} from 'lucide-react';
import axios from 'axios';
import DrillDownModal from '../DrillDownModal';
import ChartZoomModal, { ChartZoomButton } from '../ChartZoomModal';
import DeepAIInsights from '../DeepAIInsights';
import DashboardFilters from '../DashboardFilters';

import { API_BASE_URL as API } from '../../utils/apiConfig';

// Animated Points Counter
const AnimatedCounter = ({ value, duration = 1000 }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    let start = 0;
    const end = value;
    const increment = end / (duration / 16);
    
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    
    return () => clearInterval(timer);
  }, [value, duration]);
  
  return <span>{count.toLocaleString()}</span>;
};

// Liability Gauge Component (Half Circle)
const LiabilityGauge = ({ value, max, label }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const angle = (percentage / 100) * 180;
  
  const getColor = (pct) => {
    if (pct < 40) return '#10B981';
    if (pct < 70) return '#F59E0B';
    return '#EF4444';
  };
  
  const radius = 80;
  const centerX = 100;
  const centerY = 90;
  
  // Calculate arc path
  const startAngle = Math.PI;
  const endAngle = Math.PI - (angle * Math.PI / 180);
  
  const x1 = centerX + radius * Math.cos(startAngle);
  const y1 = centerY - radius * Math.sin(startAngle);
  const x2 = centerX + radius * Math.cos(endAngle);
  const y2 = centerY - radius * Math.sin(endAngle);
  
  const largeArc = angle > 90 ? 1 : 0;
  
  return (
    <div className="relative flex flex-col items-center">
      <svg width="200" height="110" viewBox="0 0 200 110">
        {/* Background arc */}
        <path
          d={`M 20 90 A 80 80 0 0 1 180 90`}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth="16"
          strokeLinecap="round"
        />
        {/* Value arc */}
        <path
          d={`M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 0 ${x2} ${y2}`}
          fill="none"
          stroke={getColor(percentage)}
          strokeWidth="16"
          strokeLinecap="round"
          className="transition-all duration-1000"
        />
        {/* Center decoration */}
        <circle cx={centerX} cy={centerY} r="8" fill={getColor(percentage)} />
      </svg>
      <div className="absolute bottom-0 text-center">
        <p className="text-2xl font-bold" style={{ color: getColor(percentage) }}>
          {percentage.toFixed(0)}%
        </p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </div>
  );
};

// Health Status Badge
const HealthBadge = ({ status }) => {
  const config = {
    healthy: { color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle, label: 'Healthy' },
    low: { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: AlertTriangle, label: 'Below Benchmark' },
    high: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertCircle, label: 'Above Benchmark' }
  };
  
  const { color, icon: Icon, label } = config[status] || config.healthy;
  
  return (
    <Badge variant="outline" className={`${color} gap-1`}>
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
};

// Points Flow Sankey-like visualization
const PointsFlowDiagram = ({ earned, redeemed, expired, outstanding }) => {
  const total = earned;
  const redeemedPct = (redeemed / total) * 100;
  const expiredPct = (expired / total) * 100;
  const outstandingPct = (outstanding / total) * 100;
  
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="w-24 text-xs text-muted-foreground">Earned</div>
        <div className="flex-1 h-8 bg-blue-500 rounded-lg flex items-center justify-center text-white text-xs font-semibold">
          {(total / 1000000).toFixed(1)}M
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-24 text-xs text-muted-foreground">Redeemed</div>
        <div 
          className="h-8 bg-emerald-500 rounded-lg flex items-center justify-center text-white text-xs font-semibold"
          style={{ width: `${Math.max(redeemedPct, 10)}%` }}
        >
          {redeemedPct.toFixed(0)}%
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-24 text-xs text-muted-foreground">Expired</div>
        <div 
          className="h-8 bg-red-500 rounded-lg flex items-center justify-center text-white text-xs font-semibold"
          style={{ width: `${Math.max(expiredPct, 10)}%` }}
        >
          {expiredPct.toFixed(0)}%
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-24 text-xs text-muted-foreground">Outstanding</div>
        <div 
          className="h-8 bg-amber-500 rounded-lg flex items-center justify-center text-white text-xs font-semibold"
          style={{ width: `${Math.max(outstandingPct, 10)}%` }}
        >
          {outstandingPct.toFixed(0)}%
        </div>
      </div>
    </div>
  );
};

// Aging Bucket Card
const AgingBucketCard = ({ bucket, onClick }) => {
  const riskColors = {
    low: { bg: 'from-emerald-500/20 to-teal-500/10', border: 'border-emerald-500/30', icon: Shield },
    medium: { bg: 'from-amber-500/20 to-orange-500/10', border: 'border-amber-500/30', icon: Timer },
    high: { bg: 'from-red-500/20 to-pink-500/10', border: 'border-red-500/30', icon: AlertTriangle }
  };
  
  const { bg, border, icon: Icon } = riskColors[bucket.risk_level] || riskColors.low;
  
  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
    return `₹${Math.round(value || 0).toLocaleString()}`;
  };
  
  return (
    <Card 
      className={`cursor-pointer hover:shadow-lg transition-all bg-gradient-to-br ${bg} ${border}`}
      onClick={() => onClick(bucket)}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">{bucket.bucket}</span>
          <Icon className={`w-4 h-4 ${bucket.risk_level === 'high' ? 'text-red-400' : bucket.risk_level === 'medium' ? 'text-amber-400' : 'text-emerald-400'}`} />
        </div>
        <p className="text-2xl font-bold">{formatCurrency(bucket.value)}</p>
        <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
          <span>{bucket.customers.toLocaleString()} customers</span>
          <span>{bucket.pct_of_liability}% of total</span>
        </div>
        <Progress 
          value={bucket.pct_of_liability} 
          className="mt-2 h-1.5" 
        />
      </CardContent>
    </Card>
  );
};

export default function PointsEconomicsDashboard() {
  const { selectedBrand } = useChat();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('overview'); // overview, flow, aging, velocity
  const [drillDown, setDrillDown] = useState({ open: false, type: null, data: null, title: '' });
  const [isLiveData, setIsLiveData] = useState(false);
  const [filters, setFilters] = useState({ dateRange: 'lifetime', city: 'all', store: 'all' });
  const [filterOptions, setFilterOptions] = useState({ cities: [], stores: [] });
  const [chartZoom, setChartZoom] = useState({ open: false, type: '', data: [], title: '', config: {} });

  // Chart zoom handlers
  const openChartZoom = (chartType, chartData, chartTitle, chartConfig = {}) => {
    setChartZoom({ open: true, type: chartType, data: chartData, title: chartTitle, config: chartConfig });
  };
  const closeChartZoom = () => setChartZoom({ open: false, type: '', data: [], title: '', config: {} });

  // Check for live data
  const getDbBrandId = useCallback(() => {
    return selectedBrand?.db_brand_id || selectedBrand?.settings?.db_brand_id || null;
  }, [selectedBrand]);

  const fetchData = useCallback(async () => {
    if (!selectedBrand) return;
    
    setLoading(true);
    setError(null);
    
    // Check for live Data Bridge - mark as live if brand has db_brand_id
    const dbBrandId = getDbBrandId();
    // Live status set after response
    // Add cache-busting to ensure fresh data
    const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Use original API for proper data structure
    try {
      const response = await axios.post(
        `${API}/api/analytics/points-economics?_t=${Date.now()}&_rid=${requestId}`,
        { brand_id: selectedBrand.brand_id, filters },
        { 
          withCredentials: true,
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'X-Request-ID': requestId
          }
        }
      );
      
      // Force new object reference
      setData({...response.data.data});
      setIsLiveData(response.data.data?.source === 'live_data_bridge' || !!dbBrandId);
      
      // Extract filter options from response
      if (response.data.data?.filter_context) {
        setFilterOptions({
          cities: response.data.data.filter_context.cities || [],
          stores: response.data.data.filter_context.stores || []
        });
      }
    } catch (err) {
      console.error('Points economics data error:', err);
      setError('Failed to load points economics data');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, getDbBrandId, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = () => {
    if (!data) return;
    const csvContent = [
      ['KPI', 'Value', 'Change'],
      ...(data.kpis || []).map(k => [k.label, k.formatted, k.delta || 'N/A'])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `points_economics_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const openDrillDown = (type, value, title, context = {}) => {
    setDrillDown({ open: true, type, data: { value, context }, title });
  };

  const closeDrillDown = () => {
    setDrillDown({ open: false, type: null, data: null, title: '' });
  };

  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
    return `₹${Math.round(value || 0).toLocaleString()}`;
  };

  const formatPoints = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return Math.round(value || 0).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="points-economics-loading">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <Coins className="w-16 h-16 text-amber-500 animate-bounce" />
            <div className="absolute -top-2 -right-2">
              <Sparkles className="w-6 h-6 text-yellow-400 animate-pulse" />
            </div>
          </div>
          <p className="text-muted-foreground">Calculating points economics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-8 text-center">
          <p className="text-destructive mb-4">{error}</p>
          <Button onClick={fetchData}>Retry</Button>
        </Card>
      </div>
    );
  }

  const summary = data?.summary || {};
  const kpis = data?.kpis || [];
  const monthlyTrend = data?.monthly_trend || [];
  const agingCurve = data?.aging_curve || [];
  const velocityData = data?.velocity_data || [];
  const earnDistribution = data?.earn_distribution || [];
  const healthIndicators = data?.health_indicators || {};
  const customerStats = data?.customer_stats || {};
  const insights = data?.insights || {};

  // KPI Icon mapping
  const KPI_ICONS = {
    'wallet': Wallet,
    'refresh-cw': RefreshCw,
    'trending-down': TrendingDown,
    'users': Users,
    'zap': Zap,
    'alert-triangle': AlertTriangle
  };

  return (
    <div className="space-y-6" data-testid="points-economics-dashboard">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-amber-500 via-yellow-500 to-orange-500 shadow-lg shadow-amber-500/30">
              <PiggyBank className="w-6 h-6 text-white" />
            </div>
            Points Economics
            {isLiveData && (
              <Badge className="ml-2 bg-emerald-500 text-white animate-pulse">
                <Zap className="w-3 h-3 mr-1" />
                LIVE
              </Badge>
            )}
            {data?.data_as_of && (
              <Badge className="ml-2 bg-blue-500/80 text-white">
                <Clock className="w-3 h-3 mr-1" />
                Data as of: {data.data_as_of}
              </Badge>
            )}
          </h2>
          <p className="text-muted-foreground">
            {isLiveData 
              ? `Live data: ₹${((data?.summary?.outstanding_liability || 0)/10000000).toFixed(1)}Cr liability`
              : 'Points liability, breakage analysis & redemption economics'
            }
          </p>
        </div>
        
        <div className="flex items-center bg-muted rounded-lg p-1">
          <Button 
            variant={viewMode === 'overview' ? 'default' : 'ghost'} 
            size="sm"
            onClick={() => setViewMode('overview')}
          >
            Overview
          </Button>
          <Button 
            variant={viewMode === 'flow' ? 'default' : 'ghost'} 
            size="sm"
            onClick={() => setViewMode('flow')}
          >
            Points Flow
          </Button>
          <Button 
            variant={viewMode === 'aging' ? 'default' : 'ghost'} 
            size="sm"
            onClick={() => setViewMode('aging')}
          >
            Liability Aging
          </Button>
          <Button 
            variant={viewMode === 'velocity' ? 'default' : 'ghost'} 
            size="sm"
            onClick={() => setViewMode('velocity')}
          >
            Velocity
          </Button>
        </div>
      </div>

      {/* Dashboard Filters */}
      <DashboardFilters
        filters={filters}
        setFilters={setFilters}
        filterOptions={filterOptions}
        onRefresh={fetchData}
        onExport={handleExport}
        loading={loading}
      />

      {/* Hero KPI Row - Vault Styled */}
      <div className="grid lg:grid-cols-4 gap-4">
        {/* Main Liability Card */}
        <Card className="lg:col-span-2 bg-gradient-to-br from-amber-500/20 via-yellow-500/10 to-orange-500/20 border-amber-500/30">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Gem className="w-5 h-5 text-amber-500" />
                  <span className="text-sm text-muted-foreground">Outstanding Liability</span>
                </div>
                <p className="text-4xl font-bold text-amber-500">
                  {formatCurrency(summary.liability_value)}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  <AnimatedCounter value={summary.outstanding_points} /> points @ ₹{summary.point_value}/point
                </p>
              </div>
              <div className="text-right">
                <Badge variant="outline" className="mb-2 bg-amber-500/10 text-amber-400 border-amber-500/30">
                  <Scale className="w-3 h-3 mr-1" />
                  Live Liability
                </Badge>
                <p className="text-xs text-muted-foreground mt-2">After expected breakage:</p>
                <p className="text-lg font-semibold text-emerald-400">
                  {formatCurrency(summary.expected_liability_after_breakage)}
                </p>
              </div>
            </div>
            
            {/* Mini points flow */}
            <div className="mt-4 pt-4 border-t border-amber-500/20 grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-muted-foreground">Total Earned</p>
                <p className="text-lg font-semibold text-blue-400">{formatPoints(summary.total_earned)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Redeemed</p>
                <p className="text-lg font-semibold text-emerald-400">{formatPoints(summary.total_redeemed)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Expired</p>
                <p className="text-lg font-semibold text-red-400">{formatPoints(summary.total_expired)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Earn-Burn Gauge */}
        <Card className="flex flex-col items-center justify-center">
          <LiabilityGauge 
            value={summary.earn_burn_ratio} 
            max={50} 
            label="Earn-Burn Ratio"
          />
          <div className="mt-2">
            <HealthBadge status={healthIndicators.earn_burn_health} />
          </div>
        </Card>

        {/* Breakage Gauge */}
        <Card className="flex flex-col items-center justify-center">
          <LiabilityGauge 
            value={summary.breakage_rate} 
            max={50} 
            label="Breakage Rate"
          />
          <div className="mt-2">
            <HealthBadge status={healthIndicators.breakage_health} />
          </div>
        </Card>
      </div>

      {/* KPI Cards Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpis.map((kpi, idx) => {
          const Icon = KPI_ICONS[kpi.icon] || Coins;
          const metricKey = resolveMetricKey(kpi.label);
          return (
            <Card 
              key={idx}
              className="cursor-pointer hover:shadow-lg transition-all group"
              onClick={() => openDrillDown('kpi', kpi.label.toLowerCase().replace(/\s+/g, '_'), kpi.label)}
              data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-4 h-4" style={{ color: kpi.color }} />
                  <FormulaTooltip metric={metricKey} name={kpi.label}>
                    <span className="text-xs text-muted-foreground truncate">{kpi.label}</span>
                  </FormulaTooltip>
                </div>
                <p className="text-xl font-bold">{kpi.formatted}</p>
                <p className="text-xs text-muted-foreground mt-1 truncate">{kpi.subtext}</p>
                <ChevronRight className="w-4 h-4 absolute bottom-2 right-2 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Content Based on View Mode */}
      {viewMode === 'overview' && (
        <>
          {/* Earn vs Burn Trend */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="w-4 h-4 text-blue-500" />
                Monthly Earn vs Burn Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={monthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tickFormatter={(v) => `${(v/1000000).toFixed(0)}M`} tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value, name) => {
                      if (name === 'earn_burn_ratio') return [`${value}%`, 'Earn-Burn Ratio'];
                      return [formatPoints(value), name === 'earned' ? 'Earned' : name === 'redeemed' ? 'Redeemed' : 'Net'];
                    }}
                  />
                  <Legend />
                  <Bar yAxisId="left" dataKey="earned" name="Earned" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="left" dataKey="redeemed" name="Redeemed" fill="#10B981" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="earn_burn_ratio" name="Earn-Burn %" stroke="#F59E0B" strokeWidth={2} dot={{ fill: '#F59E0B' }} />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Insights Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.values(insights).slice(0, 3).map((insight, idx) => (
              <Card 
                key={idx}
                className={`cursor-pointer hover:shadow-lg transition-all bg-gradient-to-br ${
                  insight?.severity === 'success' ? 'from-emerald-500/10 to-teal-500/5 border-emerald-500/20' :
                  insight?.severity === 'warning' ? 'from-amber-500/10 to-orange-500/5 border-amber-500/20' :
                  insight?.severity === 'critical' ? 'from-red-500/10 to-pink-500/5 border-red-500/20' :
                  'from-blue-500/10 to-cyan-500/5 border-blue-500/20'
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      insight?.severity === 'success' ? 'bg-emerald-500/20' :
                      insight?.severity === 'warning' ? 'bg-amber-500/20' :
                      insight?.severity === 'critical' ? 'bg-red-500/20' :
                      'bg-blue-500/20'
                    }`}>
                      {insight?.severity === 'success' ? <CheckCircle className="w-5 h-5 text-emerald-400" /> :
                       insight?.severity === 'warning' ? <AlertTriangle className="w-5 h-5 text-amber-400" /> :
                       insight?.severity === 'critical' ? <AlertCircle className="w-5 h-5 text-red-400" /> :
                       <Sparkles className="w-5 h-5 text-blue-400" />}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm mb-1">{insight?.title}</h4>
                      <p className="text-xs text-muted-foreground">{insight?.insight}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Customer Stats Row */}
          <div className="grid md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/5 border-blue-500/20">
              <CardContent className="p-4 text-center">
                <Users className="w-8 h-8 mx-auto mb-2 text-blue-400" />
                <p className="text-2xl font-bold">{customerStats.total_earning_customers?.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Customers Who Earned</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border-emerald-500/20">
              <CardContent className="p-4 text-center">
                <Target className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                <p className="text-2xl font-bold">{customerStats.total_redeeming_customers?.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Customers Who Redeemed</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border-amber-500/20">
              <CardContent className="p-4 text-center">
                <Clock className="w-8 h-8 mx-auto mb-2 text-amber-400" />
                <p className="text-2xl font-bold">{customerStats.never_redeemed?.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Never Redeemed</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-red-500/10 to-pink-500/5 border-red-500/20">
              <CardContent className="p-4 text-center">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-red-400" />
                <p className="text-2xl font-bold">{customerStats.affected_by_expiry?.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Lost Points to Expiry</p>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {viewMode === 'flow' && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Points Flow Diagram */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="w-4 h-4 text-blue-500" />
                Points Lifecycle Flow
              </CardTitle>
            </CardHeader>
            <CardContent>
              <PointsFlowDiagram 
                earned={summary.total_earned}
                redeemed={summary.total_redeemed}
                expired={summary.total_expired}
                outstanding={summary.outstanding_points}
              />
              <div className="mt-4 pt-4 border-t border-border space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Redemption Participation</span>
                  <span className="font-medium">{summary.redemption_participation}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Points per Transaction</span>
                  <span className="font-medium">{summary.points_per_txn}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Revenue per Point</span>
                  <span className="font-medium">₹{summary.revenue_per_point}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Earn Distribution */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart className="w-4 h-4 text-violet-500" />
                Points Earned Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={earnDistribution} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="range" width={60} tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="count" name="Customers" fill="#8B5CF6" radius={[0, 4, 4, 0]}>
                    {earnDistribution.map((entry, index) => (
                      <Cell key={index} fill={['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444'][index % 5]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Remaining Insights */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-yellow-500" />
                AI Insights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {Object.values(insights).slice(3).map((insight, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 rounded-lg bg-gradient-to-br ${
                      insight?.severity === 'success' ? 'from-emerald-500/10 to-transparent' :
                      insight?.severity === 'warning' ? 'from-amber-500/10 to-transparent' :
                      insight?.severity === 'critical' ? 'from-red-500/10 to-transparent' :
                      'from-blue-500/10 to-transparent'
                    }`}
                  >
                    <h4 className="font-semibold text-sm mb-1">{insight?.title}</h4>
                    <p className="text-xs text-muted-foreground mb-2">{insight?.insight}</p>
                    {insight?.recommendation && (
                      <p className="text-xs text-primary italic">💡 {insight.recommendation}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {viewMode === 'aging' && (
        <>
          {/* Aging Curve Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-500" />
                Liability Aging Curve
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={agingCurve}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value, name) => [formatCurrency(value), 'Liability Value']}
                  />
                  <Bar dataKey="value" name="Liability Value" radius={[4, 4, 0, 0]}>
                    {agingCurve.map((entry, index) => (
                      <Cell 
                        key={index} 
                        fill={entry.risk_level === 'high' ? '#EF4444' : entry.risk_level === 'medium' ? '#F59E0B' : '#10B981'} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Aging Bucket Cards */}
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
            {agingCurve.map((bucket, idx) => (
              <AgingBucketCard 
                key={idx} 
                bucket={bucket}
                onClick={(b) => openDrillDown('aging_bucket', b.bucket, `Liability: ${b.bucket}`)}
              />
            ))}
          </div>

          {/* Risk Distribution */}
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border-emerald-500/20">
              <CardContent className="p-4 text-center">
                <Shield className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                <p className="text-2xl font-bold text-emerald-400">{formatCurrency(healthIndicators.liability_risk_distribution?.low)}</p>
                <p className="text-sm text-muted-foreground">Low Risk (0-90 days)</p>
                <p className="text-xs text-muted-foreground mt-1">Likely to be redeemed soon</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border-amber-500/20">
              <CardContent className="p-4 text-center">
                <Timer className="w-8 h-8 mx-auto mb-2 text-amber-400" />
                <p className="text-2xl font-bold text-amber-400">{formatCurrency(healthIndicators.liability_risk_distribution?.medium)}</p>
                <p className="text-sm text-muted-foreground">Medium Risk (90-365 days)</p>
                <p className="text-xs text-muted-foreground mt-1">Need re-engagement</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-red-500/10 to-pink-500/5 border-red-500/20">
              <CardContent className="p-4 text-center">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-red-400" />
                <p className="text-2xl font-bold text-red-400">{formatCurrency(healthIndicators.liability_risk_distribution?.high)}</p>
                <p className="text-sm text-muted-foreground">High Risk (1+ year)</p>
                <p className="text-xs text-muted-foreground mt-1">Likely to expire</p>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {viewMode === 'velocity' && (
        <>
          {/* Velocity Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Zap className="w-4 h-4 text-violet-500" />
                Points Velocity (Earn vs Burn Rate)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={velocityData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${(v/1000000).toFixed(1)}M`} tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => formatPoints(value)}
                  />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="earn_velocity" 
                    name="Earn Velocity"
                    stroke="#3B82F6" 
                    fill="#3B82F6" 
                    fillOpacity={0.3}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="burn_velocity" 
                    name="Burn Velocity"
                    stroke="#10B981" 
                    fill="#10B981" 
                    fillOpacity={0.3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Net Velocity */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                Net Liability Change
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={velocityData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${(v/1000000).toFixed(1)}M`} tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => formatPoints(value)}
                  />
                  <Bar dataKey="net_velocity" name="Net Change" radius={[4, 4, 0, 0]}>
                    {velocityData.map((entry, index) => (
                      <Cell 
                        key={index} 
                        fill={entry.net_velocity >= 0 ? '#F59E0B' : '#10B981'} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="flex items-center justify-center gap-6 mt-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-amber-500" />
                  <span className="text-muted-foreground">Liability Increasing</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-emerald-500" />
                  <span className="text-muted-foreground">Liability Decreasing</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Overall Summary Insight */}
      <Card className="bg-gradient-to-r from-amber-500/10 via-yellow-500/5 to-orange-500/10 border-amber-500/20">
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 shadow-lg shadow-amber-500/30">
              <PiggyBank className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold mb-2">{insights.liability_overview?.title}</h3>
              <p className="text-sm text-muted-foreground">{insights.liability_overview?.insight}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Deep AI Insights */}
      <DeepAIInsights
        brandId={selectedBrand?.brand_id}
        brandName={selectedBrand?.name}
        dashboardType="points_economics"
        dashboardData={data}
        kpis={{
          total_customers: { value: data?.totals?.total_members || 0 }
        }}
        points={{
          total_earned: data?.totals?.total_earned || 0,
          total_redeemed: data?.totals?.total_redeemed || 0,
          liability: data?.totals?.outstanding || 0,
          liability_value: (data?.totals?.outstanding || 0) * 0.25
        }}
      />

      {/* Drill Down Modal */}
      <DrillDownModal
        open={drillDown.open}
        onClose={closeDrillDown}
        drillType={drillDown.type}
        drillData={drillDown.data}
        title={drillDown.title}
        brandId={selectedBrand?.brand_id}
      />

      {/* Chart Zoom Modal */}
      <ChartZoomModal
        open={chartZoom.open}
        onClose={closeChartZoom}
        chartType={chartZoom.type}
        chartData={chartZoom.data}
        chartTitle={chartZoom.title}
        chartConfig={chartZoom.config}
        brandId={selectedBrand?.brand_id}
        dashboardType="points_economics"
      />
    </div>
  );
}
